import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, MessageCircle, Clock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import WhatsAppButton from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  "Wedding Photography",
  "Pre-Wedding Shoot",
  "Portrait Session",
  "Commercial/Brand Shoot",
  "Event Coverage",
  "Cinematic Videography",
  "Other",
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    event_type: "",
    event_date: "",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      setFormData({ ...formData, event_date: format(selectedDate, "yyyy-MM-dd") });
    }
  };

  const handleEventTypeChange = (value) => {
    setFormData({ ...formData, event_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.event_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      setSubmitted(true);
      toast.success("Thank you! Your inquiry has been submitted.");
      setFormData({
        name: "",
        phone: "",
        email: "",
        event_type: "",
        event_date: "",
        message: "",
      });
      setDate(null);
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-24">
      {/* Hero Section */}
      <section data-testid="contact-hero" className="section pb-12">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
              Get in Touch
            </span>
            <h1 className="font-serif text-5xl md:text-7xl font-medium text-white mb-6">
              Contact Us
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Ready to create something beautiful? We'd love to hear from you. 
              Send us a message and let's discuss your vision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section data-testid="contact-content" className="pb-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-serif text-3xl text-white mb-8">Let's Connect</h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={24} strokeWidth={1} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">Phone</h3>
                    <a
                      href="tel:+919417589900"
                      data-testid="contact-phone"
                      className="text-white/60 hover:text-gold transition-colors"
                    >
                      +91 9417589900
                    </a>
                    <p className="text-white/40 text-sm mt-1">Mon - Sat, 10am - 7pm</p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Mail size={24} strokeWidth={1} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">Email</h3>
                    <a
                      href="mailto:godarastudios@gmail.com"
                      data-testid="contact-email"
                      className="text-white/60 hover:text-gold transition-colors"
                    >
                      godarastudios@gmail.com
                    </a>
                    <p className="text-white/40 text-sm mt-1">We reply within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} strokeWidth={1} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">Location</h3>
                    <p className="text-white/60">Abohar,Punjab</p>
                    <p className="text-white/40 text-sm mt-1">Serving Punjab, Haryana & Rajasthan</p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Clock size={24} strokeWidth={1} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">Working Hours</h3>
                    <p className="text-white/60">Monday - Saturday</p>
                    <p className="text-white/40 text-sm mt-1">10:00 AM - 7:00 PM IST</p>
                  </div>
                </div>
              </div>

              {/* Quick WhatsApp */}
              <a
                href="https://wa.me/919417589900?text=Hello!%20I'm%20interested%20in%20booking%20a%20photography%20session."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-link"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-4 hover:bg-[#20bd5a] transition-colors"
              >
                <MessageCircle size={20} strokeWidth={1.5} />
                <span className="font-medium">Chat on WhatsApp</span>
              </a>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-surface border border-white/5 p-8 md:p-12"
            >
              {submitted ? (
                <div data-testid="form-success" className="text-center py-12">
                  <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={40} className="text-gold" />
                  </div>
                  <h3 className="font-serif text-2xl text-white mb-4">Thank You!</h3>
                  <p className="text-white/60 mb-8">
                    Your inquiry has been received. We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="submit-another-btn"
                    className="text-gold text-sm uppercase tracking-widest hover:underline"
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-serif text-2xl text-white mb-8">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                    {/* Name */}
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your Name *"
                        data-testid="input-name"
                        className="form-input"
                        required
                      />
                    </div>

                    {/* Phone & Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Phone Number *"
                        data-testid="input-phone"
                        className="form-input"
                        required
                      />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email Address *"
                        data-testid="input-email"
                        className="form-input"
                        required
                      />
                    </div>

                    {/* Event Type */}
                    <div className="relative z-20">
                      <select
                        name="event_type"
                        value={formData.event_type}
                        onChange={(e) => handleEventTypeChange(e.target.value)}
                        data-testid="select-event-type"
                        className="form-input bg-transparent w-full cursor-pointer appearance-none"
                        required
                      >
                        <option value="" disabled className="bg-surface text-white/50">Select Event Type *</option>
                        {eventTypes.map((type) => (
                          <option key={type} value={type} className="bg-surface text-white">
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Event Date */}
                    <div className="relative z-10">
                      <input
                        type="date"
                        name="event_date"
                        value={formData.event_date}
                        onChange={(e) => {
                          setFormData({ ...formData, event_date: e.target.value });
                          if (e.target.value) {
                            setDate(new Date(e.target.value));
                          }
                        }}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="date-picker-trigger"
                        className="form-input w-full cursor-pointer [color-scheme:dark]"
                        placeholder="Select Event Date (Optional)"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your vision..."
                        data-testid="input-message"
                        rows={4}
                        className="form-input resize-none"
                      />
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      data-testid="submit-btn"
                      disabled={loading}
                      className="w-full bg-gold text-black py-4 text-sm uppercase tracking-widest font-bold hover:bg-white transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Inquiry
                          <Send size={18} strokeWidth={1.5} />
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </main>
  );
}
